import React from "react";

export type LogoMarkVariant = "aubergine" | "paper" | "signal" | "cloud" | "tide";

interface LogoMarkProps {
  size?: number;
  variant?: LogoMarkVariant;
}

function MarkShell({
  size,
  children,
}: {
  size: number;
  children: React.ReactNode;
}) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 100 100"
      fill="none"
      aria-hidden="true"
      style={{ display: "block", flexShrink: 0 }}
    >
      {children}
    </svg>
  );
}

function PaperMark({ size }: { size: number; id: string }) {
  return (
    <MarkShell size={size}>
      <rect width="100" height="100" rx="25" fill="#EDE6D6" />
      <path d="M40 34 h21 v7.5 h-13.5 v5.5 h11.5 v7.5 h-11.5 v5.5 h13.5 v7.5 h-21 z" fill="#C45C26" />
      <circle cx="72" cy="28" r="5.5" fill="#C45C26" />
      <rect x="2" y="1.5" width="96" height="97" rx="23.5" fill="none" stroke="#C45C26" strokeOpacity="0.28" strokeWidth="1.4" />
    </MarkShell>
  );
}

function SignalMark({ size }: { size: number; id: string }) {
  return (
    <MarkShell size={size}>
      <rect width="100" height="100" rx="22" fill="#0A0A0A" />
      <path d="M39 33 h22 v6.5 h-14.5 v6 h12.5 v6.5 h-12.5 v6 h14.5 v6.5 h-22 z" fill="#C8FF3D" />
      <circle cx="74" cy="26" r="5" fill="#C8FF3D" />
    </MarkShell>
  );
}

function CloudMark({ size }: { size: number; id: string }) {
  return (
    <MarkShell size={size}>
      <rect width="100" height="100" rx="25" fill="#F4F8FF" />
      <rect x="2" y="2" width="96" height="97" rx="23" fill="none" stroke="#3B9EFF" strokeOpacity="0.45" strokeWidth="2" />
      <path d="M40 34 h21 v7.5 h-13.5 v5.5 h11.5 v7.5 h-11.5 v5.5 h13.5 v7.5 h-21 z" fill="#3B9EFF" />
      <circle cx="72" cy="28" r="5" fill="#3B9EFF" />
    </MarkShell>
  );
}

function TideMark({ size, id }: { size: number; id: string }) {
  return (
    <MarkShell size={size}>
      <defs>
        <radialGradient id={`tide${id}`} cx="32%" cy="18%" r="105%">
          <stop offset="0" stopColor="#1A6B5C" />
          <stop offset="1" stopColor="#04151A" />
        </radialGradient>
      </defs>
      <rect width="100" height="100" rx="25" fill={`url(#tide${id})`} />
      <circle cx="50" cy="50" r="33" fill="none" stroke="#9EE8D4" strokeOpacity="0.55" strokeWidth="4.5" />
      <path d="M40 34 h21 v7.5 h-13.5 v5.5 h11.5 v7.5 h-11.5 v5.5 h13.5 v7.5 h-21 z" fill="#F3FBFF" />
      <circle cx="72" cy="28" r="5" fill="#9EE8D4" />
    </MarkShell>
  );
}

// Default aubergine gold mark — production. Sample landings pass variant.
export function LogoMark({ size = 38, variant = "aubergine" }: LogoMarkProps) {
  // Unique gradient ids per instance so multiple marks on one page don't collide.
  const id = React.useId().replace(/:/g, "");
  if (variant === "paper") return <PaperMark size={size} id={id} />;
  if (variant === "signal") return <SignalMark size={size} id={id} />;
  if (variant === "cloud") return <CloudMark size={size} id={id} />;
  if (variant === "tide") return <TideMark size={size} id={id} />;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 100 100"
      fill="none"
      aria-hidden="true"
      style={{ display: "block", flexShrink: 0 }}
    >
      <defs>
        <radialGradient id={`aub${id}`} cx="30%" cy="18%" r="105%">
          <stop offset="0" stopColor="#5A3468" />
          <stop offset="0.52" stopColor="#331C3F" />
          <stop offset="1" stopColor="#120A18" />
        </radialGradient>
        <linearGradient id={`goldE${id}`} x1="0.2" y1="0" x2="0.7" y2="1">
          <stop offset="0" stopColor="#FDF3D6" />
          <stop offset="0.35" stopColor="#F2D896" />
          <stop offset="0.7" stopColor="#D6A94F" />
          <stop offset="1" stopColor="#B07E32" />
        </linearGradient>
        <linearGradient id={`ring${id}`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#F6E4AE" />
          <stop offset="1" stopColor="#B07E32" />
        </linearGradient>
        <radialGradient id={`core${id}`} cx="50%" cy="45%" r="55%">
          <stop offset="0" stopColor="#FFF6DE" />
          <stop offset="1" stopColor="#C99A46" />
        </radialGradient>
        <linearGradient id={`sheen${id}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#ffffff" stopOpacity="0.32" />
          <stop offset="0.42" stopColor="#ffffff" stopOpacity="0.05" />
          <stop offset="1" stopColor="#ffffff" stopOpacity="0" />
        </linearGradient>
        <radialGradient id={`spec${id}`} cx="30%" cy="20%" r="45%">
          <stop offset="0" stopColor="#ffffff" stopOpacity="0.5" />
          <stop offset="1" stopColor="#ffffff" stopOpacity="0" />
        </radialGradient>
        <mask id={`rg${id}`}>
          <rect width="100" height="100" fill="#fff" />
          <circle cx="72" cy="28" r="10" fill="#000" />
        </mask>
      </defs>

      <rect width="100" height="100" rx="25" fill={`url(#aub${id})`} />
      <rect width="100" height="100" rx="25" fill={`url(#sheen${id})`} />
      <ellipse cx="34" cy="24" rx="40" ry="24" fill={`url(#spec${id})`} />

      <circle cx="50" cy="50" r="33" fill="none" stroke={`url(#ring${id})`} strokeWidth="6" mask={`url(#rg${id})`} />
      <circle cx="50" cy="50" r="33" fill="none" stroke="#ffffff" strokeOpacity="0.28" strokeWidth="1.2" mask={`url(#rg${id})`} />

      <path
        d="M40 34 h21 v7.5 h-13.5 v5.5 h11.5 v7.5 h-11.5 v5.5 h13.5 v7.5 h-21 z"
        fill={`url(#goldE${id})`}
      />
      <path d="M40 34 h21 v3.2 h-21 z" fill="#ffffff" fillOpacity="0.45" />

      <circle cx="72" cy="28" r="5" fill={`url(#core${id})`} />

      <rect x="1.5" y="1.5" width="97" height="97" rx="23.5" fill="none" stroke="#ffffff" strokeOpacity="0.22" strokeWidth="1.4" />
    </svg>
  );
}
